import { Header } from "@/components/Header";
import { ServicesShowcase } from "@/components/ServicesShowcase";
import { PricingShowcase } from "@/components/PricingShowcase";
import { ParallaxElement } from "@/components/ParallaxElement";
import { ScrollIndicator } from "@/components/ScrollIndicator";
import { BrowserMockup } from "@/components/BrowserMockup";
import { TechMarquee } from "@/components/TechMarquee";
import { ProcessSteps } from "@/components/ProcessSteps";
import { FaqSection } from "@/components/FaqSection";
import {
  Linkedin,
  Instagram,
  Mail,
  Phone,
  MapPin,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import React, { Suspense } from "react";

const ContactForm = React.lazy(() => import("@/components/ContactForm").then(module => ({ default: module.ContactForm })));
const Globe3D = React.lazy(() => import("@/components/Globe3D").then(module => ({ default: module.Globe3D })));

export default function Index() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen relative">
      <ScrollIndicator />

      {/* Background ambient glow orbs */}
      <ParallaxElement speed={0.2} maxOffset={160} className="fixed top-[-10%] left-[-10%] -z-10">
        <div className="glow-orb w-[28rem] h-[28rem]" style={{ background: "hsl(var(--glow-1))" }} />
      </ParallaxElement>
      <ParallaxElement speed={0.3} maxOffset={160} className="fixed top-[20%] right-[-15%] -z-10">
        <div className="glow-orb w-[24rem] h-[24rem]" style={{ background: "hsl(var(--glow-2))" }} />
      </ParallaxElement>

      <Header />

      {/* Hero */}
      <section className="pt-20 pb-6 sm:pt-32 sm:pb-20 lg:pt-40 lg:pb-28 relative overflow-hidden">
        <div className="absolute inset-0 -z-10 grid-pattern" />

        <div className="container relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div className="text-center lg:text-left">
              <div className="animate-fade-up">
                <span className="eyebrow mb-4 sm:mb-6">
                  <span className="eyebrow-dot" />
                  {t("hero.eyebrow")}
                </span>
              </div>
              <div className="animate-fade-up delay-100">
                <h1 className="hero-title mb-4 sm:mb-6 text-gradient">
                  {t("hero.title")}
                </h1>
              </div>
              <div className="animate-fade-up delay-200">
                <p className="text-base sm:text-lg text-muted-foreground mb-6 sm:mb-10 max-w-xl mx-auto lg:mx-0 leading-relaxed">
                  {t("hero.description")}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start animate-fade-up delay-300">
                <a
                  href="https://wa.me/5541996749435"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="modern-btn group"
                >
                  <span className="relative z-10">{t("hero.startNow")}</span>
                </a>
                <button
                  className="secondary-btn"
                  onClick={() => document.getElementById("services")?.scrollIntoView({ behavior: "smooth" })}
                >
                  {t("hero.learnMore")}
                </button>
              </div>
            </div>

            <div className="hidden md:block animate-scale-in delay-300">
              <BrowserMockup />
            </div>
          </div>
        </div>

        <div className="mt-6 sm:mt-16 lg:mt-24 animate-fade-up delay-400">
          <TechMarquee />
        </div>
      </section>

      {/* Services */}
      <section className="py-12 sm:py-16 md:py-20 lg:py-24 services-section" id="services">
        <div className="container relative z-10">
          <div className="section-head animate-fade-up">
            <span className="eyebrow mb-5">
              <span className="eyebrow-dot" />
              {t("services.eyebrow")}
            </span>
            <h2 className="section-title mb-4">{t("services.title")}</h2>
            <p className="text-muted-foreground text-lg">{t("services.subtitle")}</p>
          </div>
          <ServicesShowcase />
        </div>
      </section>

      {/* Process */}
      <section className="py-12 sm:py-16 md:py-20 lg:py-24" id="process">
        <div className="container relative z-10">
          <div className="section-head animate-fade-up">
            <span className="eyebrow mb-5">
              <span className="eyebrow-dot" />
              {t("process.eyebrow")}
            </span>
            <h2 className="section-title mb-4">{t("process.title")}</h2>
            <p className="text-muted-foreground text-lg">{t("process.subtitle")}</p>
          </div>
          <ProcessSteps />
        </div>
      </section>

      {/* Mid-page CTA banner with 3D particle network */}
      <section className="py-10 sm:py-16 md:py-20 lg:py-24 relative overflow-hidden transformation-section">
        <div className="absolute inset-0 -z-10 grid-pattern opacity-60" />
        <div className="container relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-16 items-center">
            <div className="space-y-4 md:space-y-8 text-center md:text-left animate-slide-in-left">
              <span className="eyebrow">
                <span className="eyebrow-dot" />
                {t("cta.eyebrow")}
              </span>
              <h2 className="section-title text-2xl md:text-4xl">
                {t("cta.title")}
              </h2>
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                {t("cta.description")}
              </p>
              <div className="flex justify-center md:justify-start">
                <a
                  href="https://wa.me/5541996749435"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="modern-btn"
                >
                  {t("cta.button")}
                </a>
              </div>
            </div>
            <div className="hidden md:flex relative items-center justify-center h-[400px] lg:h-[500px] animate-scale-in delay-300">
              <div className="absolute inset-0 flex items-center justify-center">
                <Suspense fallback={<div>Carregando...</div>}>
                  <Globe3D />
                </Suspense>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-12 sm:py-16 md:py-20 lg:py-24 pricing-section" id="pricing">
        <div className="container relative z-10">
          <div className="text-center mb-8 sm:mb-12 lg:mb-16 animate-fade-up">
            <h2 className="section-title mb-6">{t("pricing.title")}</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-primary to-blue-500 mx-auto rounded-full mb-6"></div>
            <p className="text-muted-foreground max-w-3xl mx-auto text-base sm:text-lg leading-relaxed">
              {t("pricing.description")}
            </p>
          </div>
          <PricingShowcase />
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 sm:py-16 md:py-20 lg:py-24" id="faq">
        <div className="container relative z-10">
          <div className="section-head animate-fade-up">
            <span className="eyebrow mb-5">
              <span className="eyebrow-dot" />
              {t("faq.eyebrow")}
            </span>
            <h2 className="section-title mb-4">{t("faq.title")}</h2>
          </div>
          <FaqSection />
        </div>
      </section>

      {/* Contact */}
      <section className="py-12 sm:py-16 md:py-20 lg:py-24 contact-section" id="contact">
        <div className="container">
          <div className="max-w-xl mx-auto">
            <div className="section-head animate-fade-up mb-8">
              <span className="eyebrow mb-5">
                <span className="eyebrow-dot" />
                {t("contact.eyebrow")}
              </span>
              <h2 className="section-title">{t("contact.title")}</h2>
              <p className="text-muted-foreground text-lg mt-4">{t("contact.subtitle")}</p>
            </div>
            <div className="glass-card p-6 sm:p-8">
              <Suspense fallback={<div>Carregando formulário...</div>}>
                <ContactForm />
              </Suspense>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 sm:py-16 bg-muted/30 relative">
        <div className="absolute inset-0 bg-gradient-to-t from-background/50 to-transparent"></div>
        <div className="container relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-10">
            <div className="animate-fade-up">
              <h3 className="font-bold mb-3 sm:mb-6 tech-font text-xl" style={{ fontFamily: "'Michroma', sans-serif" }}>VYSER</h3>
              <p className="text-muted-foreground mb-4 sm:mb-6 leading-relaxed text-sm sm:text-base">
                {t("footer.tagline")}
              </p>
              <div className="flex gap-4">
                <a href="https://www.linkedin.com/company/vyser-development/" target="_blank" rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-all duration-300">
                  <Linkedin size={18} />
                </a>
                <a href="https://www.instagram.com/vyser.dev?igsh=MWI3M3EzMGd5Zm5r" target="_blank" rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-all duration-300">
                  <Instagram size={18} />
                </a>
              </div>
            </div>
            <div className="hidden md:block animate-fade-up delay-100">
              <h4 className="font-semibold mb-6 text-lg">{t("footer.services")}</h4>
              <ul className="space-y-3 text-muted-foreground">
                <li className="hover:text-primary transition-colors cursor-pointer">{t("services.landingPages.title")}</li>
                <li className="hover:text-primary transition-colors cursor-pointer">{t("services.institutionalSites.title")}</li>
                <li className="hover:text-primary transition-colors cursor-pointer">{t("services.mobileApps.title")}</li>
                <li className="hover:text-primary transition-colors cursor-pointer">{t("services.customSystems.title")}</li>
              </ul>
            </div>
            <div className="animate-fade-up delay-200">
              <h4 className="font-semibold mb-4 sm:mb-6 text-lg">{t("footer.contactTitle")}</h4>
              <ul className="flex flex-wrap gap-x-5 gap-y-2 text-sm sm:text-base md:flex-col md:gap-3 text-muted-foreground">
                <li>
                  <a href="tel:+5541996749435" className="flex items-center gap-2 hover:text-primary transition-colors">
                    <Phone size={16} className="shrink-0" /> (41) 99674-9435
                  </a>
                </li>
                <li>
                  <a href="mailto:vyserltda@gmail.com" className="flex items-center gap-2 hover:text-primary transition-colors">
                    <Mail size={16} className="shrink-0" /> vyserltda@gmail.com
                  </a>
                </li>
                <li className="flex items-center gap-2">
                  <MapPin size={16} className="shrink-0" /> {t("footer.location")}
                </li>
              </ul>
            </div>
          </div>
          <div className="mt-8 pt-6 sm:mt-12 sm:pt-8 border-t border-white/10 text-center text-sm text-muted-foreground animate-fade-up delay-400">
            <p>&copy; 2023 VYSER. {t("footer.rights")}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
